// ResumeRevisions.jsx
import React, { useContext, useEffect, useState } from 'react';
import { FileText, RotateCcw } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { DataContext } from '@/Components/DataContext.jsx';

const ResumeRevisions = ({ resumeId, handleBack }) => {
  const [loading, setLoading] = useState(true);
  const [revisions, setRevisions] = useState([]);

  const { setRevisionId, setResumeId } = useContext(DataContext);

  useEffect(() => {
    fetchRevisions();
  }, [resumeId]);

  const fetchRevisions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(route('revisions', resumeId));
      setRevisions(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching revisions:', error);
      toast.error('Failed to load resume revisions');
      setLoading(false);
    }
  };

  const handleRestoreRevision = async (resumeId, revisionId) => {
    if (
      confirm(
        'Are you sure you want to restore this revision? This will replace your current resume.'
      )
    ) {
      setLoading(true);
      try {
        // Set the resume ID first, which will trigger our isResumeSwitch flag in DataContext
        setResumeId(resumeId);
        setRevisionId(revisionId);
        handleBack();
      } catch (error) {
        console.error('Error restoring revision:', error);
        toast.error('Failed to restore revision');
        setLoading(false);
      }
    }
  };

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now - date;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    // If older than 10 days, use the standard date format
    if (diffInDays >= 10) {
      return formatDate(dateString);
    }

    // For more recent dates, use relative time
    if (diffInDays > 0) {
      // More than a day ago
      const hours = Math.floor((diffInMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      return (
        <div className="text-sm text-gray-500 dark:text-gray-400">
          <span className="font-medium">
            {diffInDays} day{diffInDays !== 1 ? 's' : ''} {hours} hr{hours !== 1 ? 's' : ''} ago
          </span>
        </div>
      );
    }

    // Less than a day ago
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    if (diffInHours > 0) {
      const minutes = Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60));
      return (
        <div className="text-sm text-gray-500 dark:text-gray-400">
          <span className="font-medium">
            {diffInHours} hr{diffInHours !== 1 ? 's' : ''} {minutes} min{minutes !== 1 ? 's' : ''}{' '}
            ago
          </span>
        </div>
      );
    }

    // Less than an hour ago
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    if (diffInMinutes > 0) {
      return (
        <div className="text-sm text-gray-500 dark:text-gray-400">
          <span className="font-medium">
            {diffInMinutes} min{diffInMinutes !== 1 ? 's' : ''} ago
          </span>
        </div>
      );
    }

    // Less than a minute ago
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400">
        <span className="font-medium">Just now</span>
      </div>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const timeStr = date.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: 'numeric',
    });
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400">
        <div>{dateStr}</div>
        <div>{timeStr}</div>
      </div>
    );
  };

  // Add tooltip with exact date for relative times
  const formatDateWithTooltip = (dateString) => {
    const relativeTime = formatRelativeTime(dateString);
    const fullDate = new Date(dateString).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    });

    return <div title={fullDate}>{relativeTime}</div>;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <div className="bg-amber-50 dark:bg-amber-900/30 p-4 mb-4 rounded-md border border-amber-200 dark:border-amber-700">
        <p className="text-amber-800 dark:text-amber-300 text-sm">
          Select a revision to restore. This will replace your current resume with the selected
          revision.
        </p>
      </div>

      <div className="overflow-hidden border border-gray-200 dark:border-gray-700 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {revisions.map((revision) => (
              <tr key={revision.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                <td className="px-6 py-4 whitespace-nowrap">
                  {formatDateWithTooltip(revision.created_at)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => handleRestoreRevision(revision.resume_id, revision.id)}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-indigo-600 dark:focus:ring-offset-gray-800 focus:outline-none transition-colors duration-200"
                  >
                    <RotateCcw className="w-4 h-4 mr-1" />
                    Restore
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-2 px-4 py-2 mr-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-indigo-600 dark:focus:ring-offset-gray-800 transition-colors duration-200"
        >
          <FileText className="w-4 h-4" />
          Back to Resumes
        </button>
      </div>
    </div>
  );
};

export default ResumeRevisions;
